import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Snowflake, ShieldCheck, FileText } from "lucide-react";
import PortalHeader from "@/components/PortalHeader";
import LegalFooter from "@/components/LegalFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { WINTERIZING_POLICY_INTRO, WINTERIZING_POLICY_POINTS } from "@/lib/content";
import api, { apiError } from "@/lib/api";
import { LastDayDateField, isLateSeasonDate } from "@/components/LastDayDateField";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const VERIFY_KEYS = [
  "name", "email", "phone", "mailing_address", "city", "state", "zip_code",
  "trailer_make", "trailer_model", "resort", "lot_number", "key_location", "last_day_at_resort",
];

export default function WinterizingSignup() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [season, setSeason] = useState(null);
  const [info, setInfo] = useState({ ...user });
  const [addons, setAddons] = useState([]);
  const [waterFilter, setWaterFilter] = useState(false);
  const [slideoutsIn, setSlideoutsIn] = useState(true);
  const [terms, setTerms] = useState(false);
  const [auto, setAuto] = useState(false);
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lateDateAcknowledged, setLateDateAcknowledged] = useState(false);

  useEffect(() => {
    api.get("/customer/season").then((r) => {
      setSeason(r.data);
      setAuto(r.data.auto_enroll);
      setQuote(r.data.base_quote);
      if (r.data.selections) {
        setAddons(r.data.selections.selected_addons || []);
        setWaterFilter(!!r.data.selections.water_filter);
        setSlideoutsIn(r.data.selections.slideouts_in !== false);
      }
    });
  }, []);

  const set = (k) => (v) => setInfo((p) => ({ ...p, [k]: v }));

  const recalc = async (nextAddons, nextSlideouts = slideoutsIn) => {
    try {
      const { data } = await api.post("/customer/quote", {
        selected_addons: nextAddons, slideouts_in: nextSlideouts, terms_accepted: false,
      });
      setQuote(data);
    } catch { /* ignore */ }
  };

  const toggleAddon = (key) => {
    const next = addons.includes(key) ? addons.filter((k) => k !== key) : [...addons, key];
    setAddons(next);
    recalc(next);
  };

  const submit = async () => {
    if (!terms) return toast.error("Please accept the Terms & Conditions.");
    if (!(info.key_location || "").trim()) return toast.error("Please enter the key location.");
    if (!info.last_day_at_resort) return toast.error("Please select your last day at the resort.");
    if (isLateSeasonDate(info.last_day_at_resort) && !lateDateAcknowledged) {
      return toast.error("Please reselect the date and acknowledge the late-season warning.");
    }
    setSubmitting(true);
    try {
      const payload = {};
      VERIFY_KEYS.forEach((k) => { payload[k] = info[k] ?? ""; });
      payload.late_date_acknowledged = lateDateAcknowledged;
      await api.put("/customer/profile", payload);
      await api.post("/customer/signup", {
        selected_addons: addons, water_filter: waterFilter, slideouts_in: slideoutsIn,
        terms_accepted: true, auto_enroll: auto, late_date_acknowledged: lateDateAcknowledged,
      });
      await refreshUser();
      toast.success(`You're signed up for Winterizing ${season.season_year}!`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!season) return null;
  const specific = season.customer_specific_price;

  return (
    <div className="min-h-screen bg-background">
      <PortalHeader />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <p className="font-mono-label text-xs text-muted-foreground">Winterizing {season.season_year} Signup</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-primary">Reserve your service</h1>
        </motion.div>

        <div className="mt-8 grid gap-8 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            {/* Step 1: verify info */}
            <section className="rounded-xl border border-border bg-card p-6" data-testid="verify-info-section">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">1</span>
                <h2 className="font-heading text-lg font-semibold text-primary">Please verify your information is correct</h2>
              </div>

              <p className="mt-5 font-mono-label text-[10px] text-muted-foreground">Personal Information</p>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <F label="Full name" t="verify-name" v={info.name} on={set("name")} />
                <F label="Email" t="verify-email" v={info.email} on={set("email")} />
                <F label="Phone" t="verify-phone" v={info.phone} on={set("phone")} />
                <F label="Mailing address (street)" t="verify-address" v={info.mailing_address} on={set("mailing_address")} />
                <F label="City" t="verify-city" v={info.city} on={set("city")} />
                <div className="grid grid-cols-2 gap-3">
                  <F label="State" t="verify-state" v={info.state} on={set("state")} />
                  <F label="Zip" t="verify-zip" v={info.zip_code} on={set("zip_code")} />
                </div>
              </div>

              <p className="mt-6 font-mono-label text-[10px] text-muted-foreground">RV Information</p>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <F label="Trailer make" t="verify-make" v={info.trailer_make} on={set("trailer_make")} />
                <F label="Trailer model" t="verify-model" v={info.trailer_model} on={set("trailer_model")} />
                <F label="Resort (trailer location)" t="verify-resort" v={info.resort} on={set("resort")} />
                <F label="Lot #" t="verify-lot" v={info.lot_number} on={set("lot_number")} />
                <F label="Key location (hiding spot)" t="verify-key" v={info.key_location} on={set("key_location")} required />
                <LastDayDateField value={info.last_day_at_resort} onChange={set("last_day_at_resort")}
                  onAcknowledgedChange={setLateDateAcknowledged} acknowledged={lateDateAcknowledged}
                  testId="verify-lastday" required />
              </div>
            </section>

            {/* Step 2: add-ons */}
            <section className="rounded-xl border border-border bg-card p-6" data-testid="addons-section">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">2</span>
                <h2 className="font-heading text-lg font-semibold text-primary">Add-on services</h2>
              </div>
              {specific != null && (
                <p className="mt-3 rounded-lg bg-accent/5 px-4 py-2 text-sm text-accent">
                  Your account has a special rate of {money(specific)} for the base winterizing service.
                </p>
              )}
              <div className="mt-4 space-y-3">
                {season.pricing.add_ons.map((a) => (
                  <label
                    key={a.key}
                    data-testid={`addon-${a.key}`}
                    className="flex cursor-pointer items-center justify-between rounded-lg border border-border px-4 py-3 transition-colors hover:bg-muted/40"
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox checked={addons.includes(a.key)} onCheckedChange={() => toggleAddon(a.key)} data-testid={`addon-check-${a.key}`} />
                      <span className="text-sm font-medium">{a.label}</span>
                    </div>
                    <span className="font-mono-label text-xs text-muted-foreground">+{money(a.price)}</span>
                  </label>
                ))}
              </div>

              <p className="mt-6 font-mono-label text-[10px] text-muted-foreground">Additional information</p>
              <div className="mt-3 space-y-3">
                <label className="flex cursor-pointer items-center justify-between rounded-lg border border-border px-4 py-3" data-testid="water-filter-row">
                  <div className="flex items-center gap-3">
                    <Checkbox checked={waterFilter} onCheckedChange={setWaterFilter} data-testid="water-filter-check" />
                    <span className="text-sm font-medium">Water Filter</span>
                  </div>
                  <span className="font-mono-label text-[10px] text-muted-foreground">No charge</span>
                </label>

                <div className="rounded-lg border border-border px-4 py-3" data-testid="slideouts-row">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium">Slide-outs In?</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Toggle <span className="font-semibold">ON</span> if your slide-outs will be <span className="font-semibold">IN</span>.
                        Leave <span className="font-semibold">OFF</span> if they will be <span className="font-semibold">OUT</span>.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono-label text-[10px] text-muted-foreground">{slideoutsIn ? "IN" : "OUT"}</span>
                      {slideoutsIn && (
                        <span className="font-mono-label text-[10px] text-muted-foreground" data-testid="slideouts-charge-label">
                          +{money(season.pricing.slideouts_in_price)}
                        </span>
                      )}
                      <Switch checked={slideoutsIn} onCheckedChange={(value) => {
                        setSlideoutsIn(value);
                        recalc(addons, value);
                      }} data-testid="slideouts-switch" />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Step 3: policy + terms */}
            <section className="rounded-xl border border-border bg-card p-6" data-testid="terms-section">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">3</span>
                <h2 className="font-heading text-lg font-semibold text-primary">Winterizing policy & terms</h2>
              </div>
              <div className="mt-4 max-h-64 overflow-y-auto rounded-lg border border-border bg-muted/30 p-4 text-sm leading-relaxed text-muted-foreground">
                <p className="flex items-center gap-2 font-medium text-foreground"><FileText className="h-4 w-4" /> Winterizing Policy</p>
                <p className="mt-2">{WINTERIZING_POLICY_INTRO}</p>
                <ol className="mt-3 list-decimal space-y-2 pl-5">
                  {WINTERIZING_POLICY_POINTS.map((p, i) => (
                    <li key={i} data-testid={`policy-point-${i}`}>{p}</li>
                  ))}
                </ol>
              </div>
              <label className="mt-4 flex cursor-pointer items-start gap-3" data-testid="accept-terms-label">
                <Checkbox checked={terms} onCheckedChange={setTerms} data-testid="accept-terms-checkbox" className="mt-0.5" />
                <span className="text-sm text-foreground">I have read and accept the Winterizing Policy and Terms &amp; Conditions.</span>
              </label>
              <label className="mt-3 flex cursor-pointer items-start gap-3" data-testid="auto-enroll-label">
                <Checkbox checked={auto} onCheckedChange={setAuto} data-testid="signup-auto-enroll-checkbox" className="mt-0.5" />
                <span className="text-sm text-foreground">Automatically sign me up every year until I disable this option.</span>
              </label>
            </section>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-2xl border border-border bg-primary p-6 text-primary-foreground" data-testid="signup-summary">
              <div className="flex items-center gap-2">
                <Snowflake className="h-5 w-5" />
                <span className="font-mono-label text-xs opacity-80">Order Summary</span>
              </div>
              <div className="mt-5 space-y-2 text-sm">
                <Row label={specific != null ? "Base (your rate)" : "Base winterizing"} value={money(quote?.base_component)} />
                {quote?.addons?.map((a) => <Row key={a.key} label={a.label} value={`+${money(a.price)}`} />)}
                {slideoutsIn && quote?.slideouts_in && <Row label="Slide-outs In" value={`+${money(quote.slideouts_in_price)}`} />}
              </div>
              <div className="mt-5 flex items-end justify-between border-t border-white/15 pt-4">
                <span className="text-sm opacity-80">Total</span>
                <span className="font-heading text-3xl font-bold" data-testid="signup-total">{money(quote?.total)}</span>
              </div>
              <Button
                data-testid="submit-signup-btn"
                onClick={submit}
                disabled={submitting || !terms}
                className="mt-6 h-12 w-full rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-1.5 h-4 w-4" /> Confirm & Sign Up</>}
              </Button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs opacity-70">
                <ShieldCheck className="h-3.5 w-3.5" /> Secure signup
              </p>
              {!terms && (
                <p className="mt-2 text-center text-xs text-primary-foreground/60" data-testid="terms-required-hint">
                  Accept the Terms & Conditions above to continue.
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}

function F({ label, v, on, t, required = false }) {
  return (
    <div className="space-y-2">
      <Label>{label} {required && <span className="text-destructive">*</span>}</Label>
      <Input data-testid={t} required={required} value={v || ""} onChange={(e) => on(e.target.value)} />
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="opacity-80">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
